import type { PriceLevel, ArbitrageOpportunity } from '../types/opportunity';
import { computeDisplayProperties } from '../types/opportunity';
import type { Crypto, CryptoNetwork, NetworkStatusResponse } from '../types/networkStatus';

// Exchange enum mapping (must match Rust definition order)
export const EXCHANGES = [
  'Upbit',    // 0
  'Bithumb',  // 1
  'Coinone',  // 2
  'Binance',  // 3
  'HTX',      // 4
  'GateIO',   // 5
  'KuCoin',   // 6
  'OKX',      // 7
  'MEXC',     // 8
  'Bybit',    // 9
  'Bitget',   // 10
  'Coinbase'  // 11
];

// Helper function to get exchange name for display
export const getExchangeName = (index: number): string => {
  return EXCHANGES[index] || 'Unknown';
};

// Message types matching Rust MessageType enum
export const MESSAGE_TYPES = {
  ArbitrageOpportunity: 0x00,
  ArbitrageOpportunityList: 0x01,
  ExchangeNetworkStatus: 0x02,
  ExchangeNetworkStatusList: 0x03,
} as const;

export type MessageType = typeof MESSAGE_TYPES[keyof typeof MESSAGE_TYPES];

// No mapping needed - just use the byte directly as a character

export class BinaryDeserializer {
  private buffer: DataView;
  private offset: number;

  constructor(arrayBuffer: ArrayBuffer) {
    this.buffer = new DataView(arrayBuffer);
    this.offset = 0;
  }

  reset(arrayBuffer: ArrayBuffer): void {
    this.buffer = new DataView(arrayBuffer);
    this.offset = 0;
  }

  readU8(): number {
    const value = this.buffer.getUint8(this.offset);
    this.offset += 1;
    return value;
  }

  readU16(): number {
    const value = this.buffer.getUint16(this.offset, true); // little-endian (matching Rust's to_le_bytes)
    this.offset += 2;
    return value;
  }

  readF64(): number {
    const value = this.buffer.getFloat64(this.offset, true); // little-endian (matching Rust's to_le_bytes)
    this.offset += 8;
    return value;
  }

  readU32(): number {
    const value = this.buffer.getUint32(this.offset, true); // little-endian
    this.offset += 4;
    return value;
  }

  readU64(): number {
    const value = this.buffer.getBigUint64(this.offset, true); // little-endian
    this.offset += 8;
    return Number(value); // Safe for timestamps
  }

  readString(): string {
    const length = this.readU8();
    const bytes = new Uint8Array(this.buffer.buffer, this.buffer.byteOffset + this.offset, length);
    this.offset += length;
    return new TextDecoder().decode(bytes);
  }

  readOptionalString(): string | undefined {
    const length = this.readU8();
    if (length === 0) {
      return undefined;
    }
    const bytes = new Uint8Array(this.buffer.buffer, this.buffer.byteOffset + this.offset, length);
    this.offset += length;
    return new TextDecoder().decode(bytes);
  }

  readPriceLevels(): PriceLevel[] {
    const count = this.readU8();
    const levels: PriceLevel[] = [];
    
    for (let i = 0; i < count; i++) {
      levels.push({
        price: this.readF64(),
        volume: this.readF64()
      });
    }
    
    return levels;
  }

  deserializeOpportunity(): ArbitrageOpportunity {
    const buyExchangeId = this.readU8();
    const sellExchangeId = this.readU8();
    const base = this.readString();
    const buyQuoteByte = this.readU8();
    const sellQuoteByte = this.readU8();
    const buyLevels = this.readPriceLevels();
    const sellLevels = this.readPriceLevels();
    const quoteConversionRate = this.readF64();
    const detectedAt = this.readU64();
    const updatedAt = this.readU64();
    
    // Convert bytes directly to characters (K, U, B)
    const buyQuote = String.fromCharCode(buyQuoteByte);
    const sellQuote = String.fromCharCode(sellQuoteByte);
    
    const opportunity: ArbitrageOpportunity = {
      buy_exchange: buyExchangeId,    // Keep as number (0-11)
      sell_exchange: sellExchangeId,  // Keep as number (0-11)
      base,
      buy_quote: buyQuote,  // Direct byte to char conversion
      sell_quote: sellQuote, // Direct byte to char conversion
      buy_levels: buyLevels,
      sell_levels: sellLevels,
      quote_conversion_rate: quoteConversionRate,
      detected_at: detectedAt,
      updated_at: updatedAt,
    } as ArbitrageOpportunity;
    
    // Compute display properties using the shared helper
    return computeDisplayProperties(opportunity);
  }

  deserializeOpportunityList(): ArbitrageOpportunity[] {
    const count = this.readU8();
    const opportunities: ArbitrageOpportunity[] = [];
    
    for (let i = 0; i < count; i++) {
      opportunities.push(this.deserializeOpportunity());
    }
    
    return opportunities;
  }

  deserializeNetworkStatus(): NetworkStatusResponse {
    const exchange = this.readU8();
    const cryptosCount = this.readU16(); // Changed to readU16 for 2-byte count
    const cryptos: Crypto[] = [];
    
    for (let i = 0; i < cryptosCount; i++) {
      const symbol = this.readString();
      const name = this.readString();
      const networksCount = this.readU8();
      const networks: CryptoNetwork[] = [];
      
      for (let j = 0; j < networksCount; j++) {
        const network = this.readString();
        const networkName = this.readString();
        const depositEnabled = this.readU8() === 1;
        const withdrawEnabled = this.readU8() === 1;
        const contractAddress = this.readOptionalString();
        const minDeposit = this.readF64();
        const minWithdraw = this.readF64();
        const withdrawFee = this.readF64();
        
        // Congestion enum: 0=Unknown, 1=Busy, 2=Free
        const congestionByte = this.readU8();
        let congestion: 'Unknown' | 'Busy' | 'Free';
        switch (congestionByte) {
          case 1:
            congestion = 'Busy';
            break;
          case 2:
            congestion = 'Free';
            break;
          default:
            congestion = 'Unknown';
        }
        
        const estimatedArrivalTime = this.readU32();
        const confirmations = this.readU32();
        
        networks.push({
          network,
          name: networkName,
          deposit_enabled: depositEnabled,
          withdraw_enabled: withdrawEnabled,
          contract_address: contractAddress,
          min_deposit: minDeposit || undefined,
          min_withdraw: minWithdraw || undefined,
          withdraw_fee: withdrawFee || undefined,
          congestion,
          estimated_arrival_time: estimatedArrivalTime || undefined,
          confirmations: confirmations || undefined,
        });
      }
      
      cryptos.push({
        symbol,
        name,
        networks,
      });
    }
    
    const timestamp = this.readU64();
    
    return {
      exchange,
      cryptos,
      timestamp,
    };
  }

  deserializeNetworkStatusList(): NetworkStatusResponse[] {
    const count = this.readU8();
    const statuses: NetworkStatusResponse[] = [];
    
    for (let i = 0; i < count; i++) {
      statuses.push(this.deserializeNetworkStatus());
    }
    
    return statuses;
  }

  static deserializeMessage(data: ArrayBuffer): {
    type: MessageType;
    data: ArbitrageOpportunity | ArbitrageOpportunity[] | NetworkStatusResponse | NetworkStatusResponse[];
  } {
    const view = new DataView(data);
    const messageType = view.getUint8(0);
    
    const deserializer = new BinaryDeserializer(data);
    deserializer.offset = 1; // Skip the message type byte
    
    switch (messageType) {
      case MESSAGE_TYPES.ArbitrageOpportunity:
        return {
          type: messageType,
          data: deserializer.deserializeOpportunity(),
        };
      
      case MESSAGE_TYPES.ArbitrageOpportunityList:
        return {
          type: messageType,
          data: deserializer.deserializeOpportunityList(),
        };
      
      case MESSAGE_TYPES.ExchangeNetworkStatus:
        return {
          type: messageType,
          data: deserializer.deserializeNetworkStatus(),
        };
      
      case MESSAGE_TYPES.ExchangeNetworkStatusList:
        return {
          type: messageType,
          data: deserializer.deserializeNetworkStatusList(),
        };
      
      default:
        throw new Error(`Unknown message type: ${messageType}`);
    }
  }
}